/**
 * Hook para testar webhooks diretamente do modal de configuração
 * Faz chamadas para a edge function test-webhook
 * 
 * RELEASE: 1.0.0 - 2024-12-24
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('useWebhookTest');

/**
 * Interface de configuração para teste de webhook
 */
export interface WebhookTestConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: string;
  credential_id?: string;
  tenant_id: string;
}

/**
 * Interface de resultado do teste
 */
export interface WebhookTestResult {
  success: boolean;
  statusCode?: number;
  responseTime?: number;
  headers?: Record<string, string>;
  body?: any;
  error?: string;
}

/**
 * Extrai caminhos únicos de um objeto JSON para sugestão de mapeamento
 * Retorna array de paths como "campo", "objeto.campo", "[0].campo"
 */
export function extractJsonPaths(obj: any, prefix = '', maxDepth = 3): string[] {
  const paths: string[] = [];
  
  if (maxDepth <= 0) return paths;
  
  if (Array.isArray(obj)) {
    // Para arrays, adiciona path com índice [0]
    if (obj.length > 0) {
      const firstItem = obj[0];
      if (typeof firstItem === 'object' && firstItem !== null) {
        const arrayPrefix = prefix ? `${prefix}[0]` : '[0]';
        paths.push(...extractJsonPaths(firstItem, arrayPrefix, maxDepth - 1));
      } else {
        paths.push(prefix ? `${prefix}[0]` : '[0]');
      }
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = prefix ? `${prefix}.${key}` : key;
      
      if (typeof value === 'object' && value !== null) {
        // Adiciona o path do objeto e recursivamente seus filhos
        paths.push(currentPath);
        paths.push(...extractJsonPaths(value, currentPath, maxDepth - 1));
      } else {
        // Valor primitivo, adiciona diretamente
        paths.push(currentPath);
      }
    }
  }
  
  return paths;
}

/**
 * Extrai valor de um objeto usando path (suporta dot notation e array index)
 * Ex: "data.user.name", "[0].id", "items[0].title"
 */
export function getValueByPath(obj: any, path: string): any {
  if (!path || obj === undefined || obj === null) return undefined;
  
  // Normaliza path: "[0].campo" -> "0.campo", "campo[0]" -> "campo.0"
  const normalizedPath = path
    .replace(/\[(\d+)\]/g, '.$1') // [0] -> .0
    .replace(/^\./, ''); // Remove ponto inicial
  
  const parts = normalizedPath.split('.');
  let current = obj;
  
  for (const part of parts) {
    if (current === undefined || current === null) return undefined;
    
    // Se for índice numérico, converte
    const index = parseInt(part, 10);
    if (!isNaN(index) && Array.isArray(current)) {
      current = current[index];
    } else {
      current = current[part];
    }
  }
  
  return current;
}

/**
 * Hook para testar webhooks
 */
export function useWebhookTest() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<WebhookTestResult | null>(null);
  const [suggestedPaths, setSuggestedPaths] = useState<string[]>([]);

  /**
   * Executa teste do webhook
   */
  const testWebhook = useCallback(async (config: WebhookTestConfig): Promise<WebhookTestResult> => {
    setIsLoading(true);
    setResult(null);
    setSuggestedPaths([]);

    log.debug('Iniciando teste de webhook:', config.url);

    try {
      const { data, error } = await supabase.functions.invoke('test-webhook', {
        body: config,
      });

      if (error) {
        log.error('Erro ao chamar test-webhook:', error);
        const errorResult: WebhookTestResult = {
          success: false,
          error: error.message || 'Erro ao conectar com o serviço de teste',
        };
        setResult(errorResult);
        return errorResult;
      }

      const testResult = data as WebhookTestResult;
      setResult(testResult);

      // Extrai paths sugeridos se a resposta for JSON válido
      if (testResult.body && typeof testResult.body === 'object') {
        const paths = extractJsonPaths(testResult.body);
        setSuggestedPaths(paths);
        log.debug('Paths sugeridos extraídos:', paths.length);
      }

      log.info(`Teste concluído: ${testResult.statusCode} em ${testResult.responseTime}ms`);
      return testResult;

    } catch (err) {
      log.error('Exceção ao testar webhook:', err);
      const errorResult: WebhookTestResult = {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado',
      };
      setResult(errorResult);
      return errorResult;

    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Limpa resultado anterior
   */
  const clearResult = useCallback(() => {
    setResult(null);
    setSuggestedPaths([]);
  }, []);

  return {
    isLoading,
    result,
    suggestedPaths,
    testWebhook,
    clearResult,
    // Utilitários exportados para uso externo
    extractJsonPaths,
    getValueByPath,
  };
}
